import { API, APIResponse } from "@/components/_Shared/API/API";
import AxiosInstance from "../components/Axios";
import { TaskProps } from "../interfaces/tasks_types";
import {
	CLOSE_PING_TASK,
	CREATE_TASK,
	DELETE_TASK,
	GET_TASK_BY_ID,
	GET_TASK_LIST,
	GET_TASKS_BY_ALL_USER_PROJECTS,
	GET_TASKS_BY_PROJECT,
	GET_TASKS_BY_USER,
	MARK_TASK_AS_COMPLETED,
	PING_TASK,
	POST_ASSIGNED_TASKS,
	UPDATE_TASK,
} from "./Endpoints/tasks";

/**
 * This is used to post a indivual task
 *
 * @param task - The task to create
 * @returns The status of the task creation
 */
export async function postTask(task: TaskProps): Promise<APIResponse> {
	const response = await API.post<void>(CREATE_TASK, {
		task_id: task.task_id ?? "",
		title: task.title,
		description: task.description,
		assigned_to: task.assigned_to ?? "",
		project_id: task.project_id,
		due_date: task.due_date,
		assigned_by: task.assigned_by ?? "",
		category: task.category,
		priority: task.priority,
	});

	return response;
}

export async function updateTask(task: TaskProps): Promise<APIResponse> {
	const response = await API.put<void>(
		UPDATE_TASK.replace("{id}", task.task_id ?? ""),
		{
			...task,
			assigned_to: task.assigned_to ?? "",
			assigned_by: task.assigned_by ?? "",
		},
	);
	return response;
}

export async function closeTask(task_id: string): Promise<number> {
	try {
		const encodedID = encodeURIComponent(task_id);
		await AxiosInstance.put(UPDATE_TASK.replace("{id}", encodedID), {
			is_active: false,
		});

		return 200;
	} catch (error) {
		console.error(error);
		throw error;
	}
}

export async function filterTasksByProject(
	project_id: string,
): Promise<TaskProps[]> {
	try {
		const response = await AxiosInstance.get(
			GET_TASKS_BY_PROJECT.replace("{projectId}", project_id),
		);

		return response.data;
	} catch (error) {
		console.error(error);
		throw error;
	}
}

/**
 * This gets all the tasks for the projects that the user is a part of
 *
 * @param email the user's email
 * @returns An array of tasks
 */
export async function filterTasksByAllUserProjects(
	email: string,
): Promise<TaskProps[]> {
	try {
		const response = await AxiosInstance.get(
			GET_TASKS_BY_ALL_USER_PROJECTS.replace("{email}", email),
		);

		return response.data;
	} catch (error) {
		console.error(error);
		throw error;
	}
}

export async function filterTasksByUser(email: string): Promise<TaskProps[]> {
	try {
		const response = await AxiosInstance.get(
			GET_TASKS_BY_USER.replace("{email}", email),
		);

		return response.data;
	} catch (error) {
		console.error(error);
		throw error;
	}
}

export async function getTaskByID(id: string): Promise<TaskProps> {
	try {
		const encodedID = encodeURIComponent(id);
		const response = await AxiosInstance.get(
			GET_TASK_BY_ID.replace("{id}", encodedID),
		);

		return response.data;
	} catch (error) {
		console.error(error);
		throw error;
	}
}

export async function deleteTask(id: string): Promise<number> {
	try {
		const encodedID = encodeURIComponent(id);
		await AxiosInstance.delete(DELETE_TASK.replace("{id}", encodedID));
		return 200;
	} catch (error) {
		console.error(error);
		throw error;
	}
}

export async function getTaskList(): Promise<TaskProps[]> {
	try {
		const response = await AxiosInstance.get(GET_TASK_LIST);
		return response.data;
	} catch (error: unknown) {
		throw error;
	}
}

/**
 * This is used to post a tasks based on the Drag and Drop Tasks page
 *
 * @param data - The data to post
 * @returns The status of the task posting
 */
export async function postAssignedTasks(data: any): Promise<APIResponse> {
	const response = await API.post<void>(POST_ASSIGNED_TASKS, data);
	return response;
}

export async function markTaskAsCompleted(
	task_id: string,
): Promise<APIResponse> {
	const encodedID = encodeURIComponent(task_id);
	const response = await API.get<void>(
		MARK_TASK_AS_COMPLETED.replace("{id}", encodedID),
	);
	return response;
}

export async function pingTask(task_id: string): Promise<APIResponse> {
	const encodedID = encodeURIComponent(task_id);
	const response = await API.get<void>(PING_TASK.replace("{id}", encodedID));
	return response;
}

export async function closePingTask(task_id: string): Promise<APIResponse> {
	const encodedID = encodeURIComponent(task_id);
	const response = await API.get<void>(
		CLOSE_PING_TASK.replace("{id}", encodedID),
	);
	return response;
}
